import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Backspace, CircleNotch } from "@phosphor-icons/react";
import { PICTURES } from "../../lib/kids";
import { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export function PicturePad({ value, onChange, testid = "kid-pic" }) {
  return (
    <div>
      <div className="flex justify-center gap-3 mb-4" data-testid={`${testid}-slots`}>
        {[0, 1, 2].map((i) => {
          const k = value[i];
          const P = k && PICTURES[k];
          const Icon = P?.[0];
          return (
            <motion.span key={i} animate={{ scale: k ? 1 : 0.92 }} className="h-14 w-14 rounded-2xl flex items-center justify-center bg-muted/60"
              style={P ? { background: `${P[1]}22`, color: P[1] } : undefined}>
              {P ? <Icon weight="fill" className="h-7 w-7" /> : <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />}
            </motion.span>
          );
        })}
      </div>
      <div className="grid grid-cols-4 gap-2.5">
        {Object.entries(PICTURES).map(([k, [I, c]]) => (
          <motion.button key={k} type="button" whileTap={{ scale: 0.88 }} disabled={value.length >= 3} onClick={() => onChange([...value, k])} aria-label={k} data-testid={`${testid}-${k}`}
            className="aspect-square rounded-2xl flex items-center justify-center transition-colors disabled:opacity-50" style={{ background: `${c}1A`, color: c }}>
            <I weight="fill" className="h-8 w-8" />
          </motion.button>
        ))}
      </div>
      {value.length > 0 && (
        <button type="button" onClick={() => onChange(value.slice(0, -1))} data-testid={`${testid}-undo`} className="mt-3 mx-auto h-9 px-4 rounded-full text-sm text-muted-foreground hover:bg-muted inline-flex items-center gap-1.5 w-fit flex">
          <Backspace className="h-4 w-4" />Undo
        </button>
      )}
    </div>
  );
}

export default function KidLogin({ onBack }) {
  const { kidLogin } = useAuth();
  const [username, setUsername] = useState("");
  const [pics, setPics] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);
  const go = async (seq) => {
    setBusy(true); setError("");
    try { await kidLogin(username.trim(), seq); }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); setPics([]); }
    finally { setBusy(false); }
  };
  const pick = (seq) => { setPics(seq); if (seq.length === 3) go(seq); };
  return (
    <div className="mt-6" data-testid="kid-login">
      <AnimatePresence mode="wait">
        {step === 0 ? (
          <motion.form key="u" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            onSubmit={(e) => { e.preventDefault(); if (username.trim()) setStep(1); }}>
            <label className="block text-sm font-medium mb-2">What's your username?</label>
            <input autoFocus value={username} onChange={(e) => setUsername(e.target.value)} autoCapitalize="none" autoCorrect="off" data-testid="kid-username"
              className="w-full h-14 rounded-2xl bg-muted/60 px-5 text-lg outline-none focus:ring-2 focus:ring-primary/40" placeholder="e.g. maya" />
            <button type="submit" disabled={!username.trim()} data-testid="kid-next" className="mt-4 w-full h-14 rounded-full bg-primary text-primary-foreground text-base font-semibold disabled:opacity-50 active:scale-[0.98] transition-transform">Next</button>
          </motion.form>
        ) : (
          <motion.div key="p" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <button type="button" onClick={() => { setStep(0); setPics([]); setError(""); }} className="mb-3 h-8 -ml-2 px-2 rounded-full text-sm text-muted-foreground inline-flex items-center gap-1 hover:bg-muted" data-testid="kid-back"><ArrowLeft className="h-4 w-4" />{username}</button>
            <p className="text-center text-sm font-medium mb-3">Tap your 3 secret pictures</p>
            <PicturePad value={pics} onChange={pick} />
            {busy && <p className="mt-3 text-center text-sm text-muted-foreground inline-flex w-full justify-center gap-2"><CircleNotch className="h-4 w-4 animate-spin" />Checking…</p>}
          </motion.div>
        )}
      </AnimatePresence>
      {error && <p className="mt-3 text-center text-sm text-[#FF3B30]" data-testid="kid-error">{error}</p>}
      <button type="button" onClick={onBack} data-testid="kid-grownup-link" className="mt-6 w-full text-center text-sm text-muted-foreground hover:text-foreground">I'm a grown-up</button>
    </div>
  );
}
